import React, { useEffect, useState, useRef } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { Employee } from '../types/employee';
import { Document, DocumentType } from '../types/document';
import { documentApi } from '../services/documentApi';
import { positionApi, Position } from '../services/positionApi';
import { salaryHistoryApi, SalaryHistory } from '../services/salaryHistoryApi';
import { useAlert } from '../contexts/AlertContext';
import { useAuth } from '../contexts/AuthContext';

// 은행명 select 옵션.
// GRK_hire(bank-name.util.ts)의 WORKSPACE_BANK_NAMES와 동일해야 한다.
// 두 목록이 어긋나면 hire에서 넘어온 은행명이 select에서 선택되지 않아 빈칸으로 보인다.
const BANK_NAMES = [
  '국민은행', '신한은행', '우리은행', '하나은행', '농협은행', '기업은행',
  'SC제일은행', '씨티은행', '대구은행', '부산은행', '경남은행', '광주은행',
  '전북은행', '제주은행', '산업은행', '수협은행', '우체국예금', '새마을금고',
  '신협', '카카오뱅크', '토스뱅크', '케이뱅크', '기타',
];

// 전화번호 표시 포맷팅 (01012345678 → 010-1234-5678)
function formatPhoneDisplay(tel?: string | null): string {
  if (!tel) return '';
  const digits = tel.replace(/[^\d]/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}

// 날짜 입력 포맷팅 함수
function formatDateInput(value: string): string {
  // 숫자만 추출
  const numbers = value.replace(/\D/g, '');
  
  // 자동으로 하이픈 추가 (YYYY-MM-DD 형식)
  if (numbers.length <= 4) {
    return numbers;
  } else if (numbers.length <= 6) {
    return numbers.slice(0, 4) + '-' + numbers.slice(4);
  } else {
    return numbers.slice(0, 4) + '-' + numbers.slice(4, 6) + '-' + numbers.slice(6, 8);
  }
}

// 날짜 유효성 검증 함수
function validateDate(dateString: string): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;
  
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
}

interface EmployeeFormProps {
  employee?: Employee;
  isEditing: boolean;
  mode: 'view' | 'edit' | 'new';
  onSubmit: (data: EmployeeFormData) => void;
  onCancel: () => void;
}

// 폼에서 사용하는 간단한 타입들
export interface EducationFormData {
  school?: string;
  major?: string;
  degree?: string;
  startDate?: string;
  graduationDate?: string;
}

export interface ExperienceFormData {
  company?: string;
  department?: string;
  position?: string;
  startDate?: string;
  endDate?: string;
  annualSalary?: string;
}

export interface EmployeeFormData {
  empNo: string;
  name: string;
  position: string;
  rank: string;
  department: string;
  tel: string;
  email: string;
  joinDate: string;
  endDate?: string;
  employmentType?: string;
  annualSalary?: string;
  status: string;
  ssn?: string;
  bankName?: string;
  bankAccount?: string;
  consultantIntroduction?: string;
  education: EducationFormData[];
  experience: ExperienceFormData[];
}

// 직급 리스트는 이제 API에서 가져옴 (POSITION_OPTIONS 제거됨)

const EmployeeForm: React.FC<EmployeeFormProps> = ({
  employee,
  isEditing,
  mode,
  onSubmit,
  onCancel
}) => {
  const { showToast, showConfirm } = useAlert();
  const { user } = useAuth();
  const [profileImage, setProfileImage] = React.useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = React.useState<string>('');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [imageDeleted, setImageDeleted] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // 직급 관련 상태
  const [positions, setPositions] = useState<Position[]>([]);
  const [isCustomPosition, setIsCustomPosition] = useState(false);
  const [customPositionName, setCustomPositionName] = useState('');

  // 연봉 이력 관련 상태
  const [salaryHistory, setSalaryHistory] = useState<SalaryHistory[]>([]);
  const [editingSalaryYear, setEditingSalaryYear] = useState<number | null>(null);
  const [editingSalaryValue, setEditingSalaryValue] = useState<string>('');
  const [newSalaryYear, setNewSalaryYear] = useState<number>(new Date().getFullYear());
  const [newSalaryValue, setNewSalaryValue] = useState<string>('');
  const [isAddingSalary, setIsAddingSalary] = useState(false);
  
  // 문서 관련 상태
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [newDocument, setNewDocument] = useState<{
    file: File | null;
    documentType: DocumentType;
    description: string;
  }>({
    file: null,
    documentType: DocumentType.OTHER,
    description: ''
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<EmployeeFormData>({
    defaultValues: {
      empNo: '',
      name: '',
      position: '',
      rank: '',
      department: '',
      tel: '',
      email: '',
      joinDate: '',
      endDate: '',
      employmentType: '정규직',
      annualSalary: '',
      status: 'ACTIVE',
      ssn: '',
      bankName: '',
      bankAccount: '',
      consultantIntroduction: '',
      education: [],
      experience: []
    }
  });

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation
  } = useFieldArray({
    control,
    name: 'education'
  });

  const {
    fields: experienceFields,
    append: appendExperience,
    remove: removeExperience
  } = useFieldArray({
    control,
    name: 'experience'
  });

  // 날짜 입력 핸들러
  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'joinDate' | 'endDate') => {
    const formatted = formatDateInput(e.target.value);
    setValue(fieldName, formatted);
    
    // 입력이 완성되면 (YYYY-MM-DD 형식, 10자리) 다음 필드로 포커스 이동
    if (formatted.length === 10) {
      const form = e.target.form;
      if (form) {
        const inputs = Array.from(form.querySelectorAll('input, select, textarea'));
        const currentIndex = inputs.indexOf(e.target);
        const nextInput = inputs[currentIndex + 1] as HTMLElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  // 문서 업로드 함수
  const handleDocumentUpload = async () => {
    if (!newDocument.file || !employee?.id) return;

    try {
      setUploadingDoc(true);
      const uploadedDoc = await documentApi.uploadDocument(
        employee.id,
        newDocument.file,
        {
          documentType: newDocument.documentType,
          description: newDocument.description
        }
      );
      
      // 업로드 성공 후 문서 목록 다시 로드
      const updatedDocs = await documentApi.getEmployeeDocuments(employee.id);
      setDocuments(updatedDocs);
      
      setNewDocument({
        file: null,
        documentType: DocumentType.OTHER,
        description: ''
      });
      
      // 파일 input 초기화
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
      showToast('문서가 성공적으로 업로드되었습니다.', 'success');
    } catch (error) {
      showToast('문서 업로드에 실패했습니다.', 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentDelete = async (documentId: number) => {
    const confirmed = await showConfirm('이 문서를 삭제하시겠습니까?');
    if (!confirmed) return;

    try {
      await documentApi.deleteDocument(documentId);

      // 삭제 성공 후 문서 목록 다시 로드
      if (employee?.id) {
        const updatedDocs = await documentApi.getEmployeeDocuments(employee.id);
        setDocuments(updatedDocs);
      }

      showToast('문서가 삭제되었습니다.', 'success');
    } catch (error) {
      showToast('문서 삭제에 실패했습니다.', 'error');
    }
  };

  const getDocumentTypeLabel = (type: DocumentType): string => {
    const labels: Record<DocumentType, string> = {
      [DocumentType.RESUME]: '이력서',
      [DocumentType.DIPLOMA]: '졸업증명서',
      [DocumentType.CAREER_CERT]: '경력증명서',
      [DocumentType.LICENSE]: '자격증',
      [DocumentType.SALARY_CONTRACT]: '연봉계약서',
      [DocumentType.RESIDENT_COPY]: '주민등록등초본',
      [DocumentType.BANK_ACCOUNT]: '통장사본',
      [DocumentType.PROFILE_PHOTO]: '프로필 사진',
      [DocumentType.INTRO_PPT]: '소개자료',
      [DocumentType.CONTRACT]: '근로계약서',
      [DocumentType.NDA]: '영업비밀 서약서',
      [DocumentType.PRIVACY]: '개인정보 동의서',
      [DocumentType.PROBATION]: '수습평가 동의서',
      [DocumentType.EQUIPMENT]: '전자기기 확인서',
      [DocumentType.OTHER]: '기타'
    };
    return labels[type] || type;
  };

  const [replacingDocId, setReplacingDocId] = useState<number | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleReplaceDocument = async (docId: number, file: File) => {
    try {
      await documentApi.replaceDocument(docId, file, user?.name);
      if (employee?.id) {
        const updatedDocs = await documentApi.getEmployeeDocuments(employee.id);
        setDocuments(updatedDocs);
      }
      setReplacingDocId(null);
      showToast('파일이 교체되었습니다.', 'success');
    } catch (error) {
      showToast('파일 교체에 실패했습니다.', 'error');
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      await documentApi.downloadDocument(doc.id, `${doc.originalName}${doc.fileExtension}`);
    } catch (error) {
      showToast('다운로드에 실패했습니다.', 'error');
    }
  };

  const handleDownloadAll = async () => {
    if (documents.length === 0) return;
    setDownloadingAll(true);
    try {
      await documentApi.downloadAll(documents);
    } catch (error) {
      showToast('일부 파일 다운로드에 실패했습니다.', 'error');
    } finally {
      setDownloadingAll(false);
    }
  };


  // 직급 목록 로드
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const data = await positionApi.getAll();
        setPositions(data);
      } catch (error) {
        console.error('Failed to fetch positions:', error);
      }
    };
    fetchPositions();
  }, []);

  // 직급 선택 핸들러
  const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__custom__') {
      setIsCustomPosition(true);
      setCustomPositionName('');
      setValue('position', '');
    } else {
      setIsCustomPosition(false);
      setCustomPositionName('');
      setValue('position', value);
    }
  };

  // 새 직급 저장 핸들러
  const handleSaveCustomPosition = async () => {
    if (!customPositionName.trim()) return;

    try {
      const newPosition = await positionApi.findOrCreate(customPositionName.trim());
      setPositions(prev => [...prev, newPosition].sort((a, b) => a.sortOrder - b.sortOrder));
      setValue('position', newPosition.name);
      setIsCustomPosition(false);
      setCustomPositionName('');
    } catch (error: any) {
      showToast(error.message || '직급 추가에 실패했습니다.', 'error');
    }
  };

  // 연봉 이력 로드
  const fetchSalaryHistory = async (empId: number) => {
    try {
      const data = await salaryHistoryApi.getByEmployee(empId);
      setSalaryHistory(data);
    } catch (error) {
      console.error('Failed to fetch salary history:', error);
    }
  };

  // 연봉 저장 핸들러
  const handleSaveSalary = async (year: number, salaryStr: string) => {
    if (!employee?.id) return;
    const salary = parseInt(salaryStr.replace(/,/g, ''));
    if (isNaN(salary) || salary <= 0) {
      showToast('올바른 연봉을 입력해주세요.', 'error');
      return;
    }
    try {
      await salaryHistoryApi.upsert(employee.id, { year, annualSalary: salary });
      await fetchSalaryHistory(employee.id);
      setEditingSalaryYear(null);
      setEditingSalaryValue('');
      setIsAddingSalary(false);
      setNewSalaryValue('');
    } catch (error: any) {
      showToast(error.message || '연봉 저장에 실패했습니다.', 'error');
    }
  };

  // 연봉 삭제 핸들러
  const handleDeleteSalary = async (year: number) => {
    if (!employee?.id) return;
    const confirmed = await showConfirm(`${year}년 연봉 기록을 삭제하시겠습니까?`);
    if (!confirmed) return;
    try {
      await salaryHistoryApi.delete(employee.id, year);
      await fetchSalaryHistory(employee.id);
    } catch (error: any) {
      showToast(error.message || '연봉 삭제에 실패했습니다.', 'error');
    }
  };

  // 연봉 포맷팅
  const formatSalary = (value: string) => {
    const num = value.replace(/[^\d]/g, '');
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 직원 데이터가 변경되면 폼 리셋
  useEffect(() => {
    if (employee) {
      // 직원이 바뀌면 삭제 플래그 초기화
      setImageDeleted(false);

      // 기존 프로필 이미지 설정
      if (employee.profileImageUrl) {
        setProfileImagePreview(employee.profileImageUrl);
      } else {
        setProfileImagePreview('');
      }
      
      reset({
        empNo: employee.empNo || '',
        name: employee.name || '',
        position: employee.position || '',
        rank: employee.rank || '',
        department: employee.department || '',
        tel: formatPhoneDisplay(employee.tel) || '',
        email: employee.email || '',
        joinDate: employee.joinDate ? new Date(employee.joinDate).toISOString().split('T')[0] : '',
        endDate: employee.endDate ? new Date(employee.endDate).toISOString().split('T')[0] : '',
        employmentType: employee.employmentType || '정규직',
        annualSalary: employee.annualSalary ?
          Number(employee.annualSalary).toLocaleString('ko-KR') : '',
        status: employee.status || 'ACTIVE',
        ssn: employee.ssn || '',
        bankName: employee.bankName || '',
        bankAccount: employee.bankAccount || '',
        consultantIntroduction: employee.consultantIntroduction || '',
        education: (employee.education && Array.isArray(employee.education)) ? employee.education.map(edu => ({
          school: edu.school,
          major: edu.major,
          degree: edu.degree,
          startDate: edu.startDate ? new Date(edu.startDate).toISOString().split('T')[0] : '',
          graduationDate: edu.graduationDate ? new Date(edu.graduationDate).toISOString().split('T')[0] : ''
        })) : [],
        experience: (employee.experience && Array.isArray(employee.experience)) ? employee.experience.map(exp => ({
          company: exp.company,
          department: exp.department,
          position: exp.position,
          startDate: exp.startDate ? new Date(exp.startDate).toISOString().split('T')[0] : '',
          endDate: exp.endDate ? new Date(exp.endDate).toISOString().split('T')[0] : '',
          annualSalary: exp.annualSalary ? 
            Number(exp.annualSalary).toLocaleString('ko-KR') : ''
        })) : []
      });

      // 문서 목록 로드
      if (employee.id) {
        documentApi.getEmployeeDocuments(employee.id)
          .then(docs => setDocuments(docs));
        // 연봉 이력 로드
        fetchSalaryHistory(employee.id);
      }
    } else if (mode === 'new') {
      reset({
        empNo: '',
        name: '',
        position: '',
        rank: '',
        department: '',
        tel: '',
        email: '',
          joinDate: '',
        endDate: '',
        annualSalary: '',
        status: 'ACTIVE',
        ssn: '',
        bankName: '',
        bankAccount: '',
        consultantIntroduction: '',
        education: [],
        experience: []
      });
    }
  }, [employee, mode, reset]);

  const onFormSubmit: SubmitHandler<EmployeeFormData> = (data) => {
    // 빈 문자열을 undefined로 변환 (선택사항 필드들)
    const cleanedData: any = {
      ...data,
      tel: data.tel?.replace(/[^\d]/g, '') || undefined,
      ssn: data.ssn?.trim() || undefined,
      bankName: data.bankName?.trim() || undefined,
      bankAccount: data.bankAccount?.trim() || undefined,
      consultantIntroduction: data.consultantIntroduction?.trim() || undefined,
      endDate: data.endDate?.trim() || undefined,
      // annualSalary는 연봉값으로 저장 (콤마 제거 후 숫자로 변환)
      annualSalary: data.annualSalary ? parseInt(data.annualSalary.replace(/,/g, '')) : undefined
    };

    // age 필드가 있다면 제거 (혹시라도 포함된 경우 대비)
    const { age, ...finalData } = cleanedData;
    
    onSubmit(finalData);
  };

  const addEducation = () => {
    appendEducation({ school: '', major: '', degree: '', startDate: '', graduationDate: '' } as EducationFormData);
  };

  const addExperience = () => {
    appendExperience({ company: '', department: '', position: '', startDate: '', endDate: '', annualSalary: '' } as ExperienceFormData);
  };

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 허용되는 이미지 타입 검사
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showToast('JPG, PNG, GIF, WebP 이미지 파일만 업로드 가능합니다.', 'error');
        return;
      }

      // 파일 크기 검사 (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('파일 크기는 5MB 이하여야 합니다.', 'error');
        return;
      }

      setProfileImage(file);
      setImageDeleted(false); // 새 이미지 선택 시 삭제 플래그 해제

      // 미리보기 생성
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfileImage = async () => {
    if (!profileImage || !employee?.id) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', profileImage);

      const token = localStorage.getItem('accessToken');
      // 파일은 항상 운영 서버에 저장
      const apiUrl = `https://admin.grkcon.com/api/employees/${employee.id}/profile-image`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      if (response.ok) {
        const updatedEmployee = await response.json();
        showToast('프로필 사진이 업데이트되었습니다.', 'success');

        // 미리보기를 업데이트된 이미지로 설정
        setProfileImagePreview(updatedEmployee.profileImageUrl);
        setProfileImage(null);

        // 파일 입력 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        // 에러 응답 본문 읽기
        const errorText = await response.text();
        showToast(`프로필 사진 업로드에 실패했습니다. 상태: ${response.status}`, 'error');
      }
    } catch (error) {
      showToast(`프로필 사진 업로드 중 오류가 발생했습니다.`, 'error');
    }
    setUploadingImage(false);
  };

  const deleteProfileImage = async () => {
    if (!employee?.id) return;

    const confirmed = await showConfirm('프로필 이미지를 삭제하시겠습니까?');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = `https://admin.grkcon.com/api/employees/${employee.id}/profile-image`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.ok) {
        showToast('프로필 이미지가 삭제되었습니다.', 'success');

        // 상태 업데이트
        setProfileImagePreview('');
        setProfileImage(null);
        setImageDeleted(true);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        showToast(`프로필 이미지 삭제에 실패했습니다.`, 'error');
      }
    } catch (error) {
      showToast(`프로필 이미지 삭제 중 오류가 발생했습니다.`, 'error');
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // status 필드 감시
  const currentStatus = watch('status');

  // 은행명 감시 — 표준 목록에 없는 값이 들어와도 select에서 사라지지 않게 하기 위함
  const currentBankName = watch('bankName');

  const inputClass = (hasError?: boolean) => `
    w-full px-3 py-2 border rounded-md
    ${hasError ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-200'}
    ${!isEditing ? 'bg-slate-50 text-slate-600' : ''}
    focus:outline-none focus:ring-2 transition-colors
  `;

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8 text-sm">
        {/* 기본 정보 섹션 */}
        <section>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-base">기본 정보</h3>
            {isEditing && (
              <div className="flex items-center space-x-2">
                {profileImage && (
                  <button
                    type="button"
                    onClick={uploadProfileImage}
                    disabled={uploadingImage}
                    className="px-3 py-1 text-xs font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {uploadingImage ? '업로드 중...' : '업로드'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="px-3 py-1 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                >
                  프로필 사진 변경
                </button>
              </div>
            )}
          </div>
          
          {/* 숨겨진 파일 입력 */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleProfileImageChange}
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            className="hidden"
          />
          
          {/* 프로필 이미지 미리보기 */}
          <div className="mt-4 flex flex-col items-center">
            <div className="relative">
              {profileImagePreview && profileImagePreview.trim() !== '' ? (
                // 프로필 이미지가 있을 때
                <>
                  <img
                    src={profileImagePreview}
                    alt="프로필 이미지 미리보기"
                    className="w-32 h-32 object-cover rounded-full border-4 border-slate-200"
                  />
                  {profileImage && !uploadingImage && (
                    <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                      업로드 대기
                    </div>
                  )}
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                      <div className="text-white text-xs">업로드 중...</div>
                    </div>
                  )}
                </>
              ) : (
                // 프로필 이미지가 없을 때 - 이니셜 아이콘 표시
                <div className="w-32 h-32 rounded-full border-4 border-slate-200 bg-indigo-100 flex items-center justify-center">
                  <span className="text-4xl font-bold text-indigo-600">
                    {(employee?.name || watch('name')) ? (employee?.name || watch('name')).charAt(0) : '?'}
                  </span>
                </div>
              )}
            </div>
            {isEditing && profileImagePreview && !profileImage && !uploadingImage && (
              <button
                type="button"
                onClick={deleteProfileImage}
                className="mt-2 px-3 py-1 text-xs text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
              >
                이미지 삭제
              </button>
            )}
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium text-slate-600 mb-1">이름 *</label>
              <input
                type="text"
                {...register('name', { 
                  required: '이름은 필수 입력 항목입니다.',
                  minLength: { value: 2, message: '이름은 최소 2자 이상이어야 합니다.' },
                  maxLength: { value: 50, message: '이름은 최대 50자 이하여야 합니다.' },
                  pattern: { 
                    value: /^[가-힣a-zA-Z\s]+$/, 
                    message: '이름은 한글, 영문, 공백만 입력 가능합니다.' 
                  }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.name)}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">
                사번 {mode === 'new' && <span className="text-xs text-slate-500">(자동 생성)</span>}
              </label>
              <input
                type="text"
                {...register('empNo')}
                readOnly={true}
                value={mode === 'new' ? '자동 생성됩니다' : undefined}
                placeholder={mode === 'new' ? '자동 생성됩니다' : ''}
                className={`${inputClass(false)} bg-slate-50 text-slate-600`}
              />
              {mode === 'new' && (
                <p className="mt-1 text-xs text-slate-500">입사 연도 + 순번으로 자동 생성됩니다 (예: 2025001)</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">직급</label>
              {isEditing ? (
                isCustomPosition ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customPositionName}
                      onChange={(e) => setCustomPositionName(e.target.value)}
                      className={`flex-1 ${inputClass(false)}`}
                      placeholder="새 직급명 입력"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveCustomPosition();
                        }
                        if (e.key === 'Escape') {
                          setIsCustomPosition(false);
                          setCustomPositionName('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSaveCustomPosition}
                      className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      저장
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomPosition(false);
                        setCustomPositionName('');
                      }}
                      className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <select
                    value={watch('position') || ''}
                    onChange={handlePositionChange}
                    className={inputClass(!!errors.position)}
                  >
                    <option value="">직급 선택</option>
                    {positions.map((pos) => (
                      <option key={pos.id} value={pos.name}>
                        {pos.name}
                      </option>
                    ))}
                    <option value="__custom__">+ 직접 입력</option>
                  </select>
                )
              ) : (
                <input
                  type="text"
                  value={watch('position') || ''}
                  readOnly
                  className={`${inputClass(false)} bg-slate-50 text-slate-600`}
                />
              )}
              {/* hidden input for form registration */}
              <input type="hidden" {...register('position')} />
              {errors.position && (
                <p className="mt-1 text-sm text-red-600">{errors.position.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">부서 *</label>
              <input
                type="text"
                {...register('department', {
                  required: '부서는 필수 입력 항목입니다.',
                  minLength: { value: 1, message: '부서는 최소 1자 이상이어야 합니다.' },
                  maxLength: { value: 50, message: '부서는 최대 50자 이하여야 합니다.' }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.department)}
              />
              {errors.department && (
                <p className="mt-1 text-sm text-red-600">{errors.department.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">직책</label>
              <input
                type="text"
                {...register('rank', {
                  maxLength: { value: 50, message: '직책은 최대 50자 이하여야 합니다.' }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.rank)}
              />
              {errors.rank && (
                <p className="mt-1 text-sm text-red-600">{errors.rank.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">
                전화번호 {currentStatus !== 'VIRTUAL' && '*'}
              </label>
              <input
                type="text"
                {...register('tel', {
                  required: currentStatus === 'VIRTUAL' ? false : '전화번호는 필수 입력 항목입니다.',
                  pattern: { value: /^[0-9-]+$/, message: '전화번호는 숫자와 하이픈만 입력 가능합니다.' },
                  minLength: { value: 10, message: '전화번호는 최소 10자 이상이어야 합니다.' },
                  maxLength: { value: 20, message: '전화번호는 최대 20자 이하여야 합니다.' }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.tel)}
                placeholder="예: 010-1234-5678"
                onInput={(e) => {
                  if (isEditing) {
                    const target = e.target as HTMLInputElement;
                    // 숫자만 추출
                    let value = target.value.replace(/[^\d]/g, '');
                    
                    // 길이에 따라 하이픈 추가
                    if (value.length <= 3) {
                      // 3자리 이하: 그대로
                      target.value = value;
                    } else if (value.length <= 7) {
                      // 4-7자리: 010-1234 형태
                      target.value = `${value.slice(0, 3)}-${value.slice(3)}`;
                    } else {
                      // 8자리 이상: 010-1234-5678 형태
                      target.value = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
                    }
                  }
                }}
              />
              {errors.tel && (
                <p className="mt-1 text-sm text-red-600">{errors.tel.message}</p>
              )}
            </div>


            <div className="md:col-span-2">
              <label className="block font-medium text-slate-600 mb-1">이메일 주소 *</label>
              <input
                type="email"
                {...register('email', {
                  required: '이메일은 필수 입력 항목입니다.',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '올바른 이메일 형식이 아닙니다.' }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.email)}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">주민등록번호</label>
              <input
                type="text"
                {...register('ssn', {
                  pattern: {
                    value: /^(\d{6}-\d{7})?$/,
                    message: "주민등록번호 '123456-1234567' 형식이어야 합니다."
                  }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.ssn)}
                placeholder="예: 900101-1234567"
                maxLength={14}
                onInput={(e) => {
                  if (isEditing) {
                    const target = e.target as HTMLInputElement;
                    // 숫자만 추출
                    let value = target.value.replace(/[^\d]/g, '');
                    
                    // 길이에 따라 하이픈 추가
                    if (value.length <= 6) {
                      target.value = value;
                    } else {
                      // 6자리 이후에 하이픈 추가: 123456-1234567
                      target.value = `${value.slice(0, 6)}-${value.slice(6, 13)}`;
                    }
                  }
                }}
              />
              {errors.ssn && (
                <p className="mt-1 text-sm text-red-600">{errors.ssn.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">연봉 *</label>
              <input
                type="text"
                {...register('annualSalary', {
                  pattern: { 
                    value: /^[\d,]+$/, 
                    message: '연봉은 숫자와 콤마만 입력 가능합니다.' 
                  },
                  validate: (value) => {
                    if (!value) return true; // 선택사항이므로 빈 값 허용
                    const numValue = parseInt(value.replace(/,/g, ''));
                    if (numValue < 0) return '연봉은 0원 이상이어야 합니다.';
                    if (numValue > 999999999999) return '연봉이 너무 큽니다.';
                    return true;
                  }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.annualSalary)}
                placeholder="예: 48,000,000"
                onInput={(e) => {
                  if (isEditing) {
                    const target = e.target as HTMLInputElement;
                    // 숫자만 추출
                    const value = target.value.replace(/[^\d]/g, '');
                    // 세자리마다 콤마 추가
                    const formattedValue = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    target.value = formattedValue;
                  }
                }}
              />
              {errors.annualSalary && (
                <p className="mt-1 text-sm text-red-600">{errors.annualSalary.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">입사일 *</label>
              <input
                type="date"
                min="1950-01-01"
                max="9999-12-31"
                {...register('joinDate', { 
                  required: '입사일은 필수 입력 항목입니다.'
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.joinDate)}
              />
              {errors.joinDate && (
                <p className="mt-1 text-sm text-red-600">{errors.joinDate.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">
                {watch('employmentType') === '계약직' || watch('employmentType') === '인턴'
                  ? '계약 종료일'
                  : <span>퇴사일 <span className="text-xs text-slate-500">(퇴사 처리 시 자동 설정)</span></span>
                }
              </label>
              <input
                type="date"
                min="1950-01-01"
                max="9999-12-31"
                {...register('endDate')}
                readOnly={watch('employmentType') === '정규직' || watch('employmentType') == null}
                className={`${inputClass(!!errors.endDate)} ${watch('employmentType') === '정규직' || watch('employmentType') == null ? 'bg-slate-50 text-slate-600' : ''}`}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">상태</label>
              <select
                {...register('status')}
                disabled={!isEditing || currentStatus === 'ON_LEAVE' || currentStatus === 'RESIGNED'}
                className={inputClass(!!errors.status)}
              >
                <option value="ACTIVE">재직</option>
                <option value="VIRTUAL">가상</option>
                <option value="INACTIVE">비활성</option>
                {/* 퇴사·휴직은 전용 버튼으로만 전환. 현재 상태일 때만 표시(읽기전용) */}
                {currentStatus === 'ON_LEAVE' && <option value="ON_LEAVE">휴직</option>}
                {currentStatus === 'RESIGNED' && <option value="RESIGNED">퇴사</option>}
              </select>
              {(currentStatus === 'ON_LEAVE' || currentStatus === 'RESIGNED') && (
                <p className="mt-1 text-xs text-slate-500">
                  {currentStatus === 'RESIGNED'
                    ? '퇴사 상태는 [퇴사 취소] 버튼으로만 변경할 수 있습니다.'
                    : '휴직 상태는 [복직] 버튼으로만 변경할 수 있습니다.'}
                </p>
              )}
              {errors.status && (
                <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">고용형태</label>
              <select
                {...register('employmentType')}
                disabled={!isEditing}
                className={inputClass(false)}
              >
                <option value="정규직">정규직</option>
                <option value="계약직">계약직</option>
                <option value="인턴">인턴</option>
              </select>
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">은행명</label>
              <select
                {...register('bankName')}
                disabled={!isEditing}
                className={inputClass(!!errors.bankName)}
              >
                <option value="">은행 선택</option>
                {/* 목록에 없는 값(예: hire OCR의 'IBK 기업은행')도 보이도록 임시 옵션을 추가한다.
                    이게 없으면 select가 빈칸으로 표시되고 저장 시 기존 은행명이 지워진다. */}
                {currentBankName && !BANK_NAMES.includes(currentBankName) && (
                  <option value={currentBankName}>{currentBankName} (확인 필요)</option>
                )}
                {BANK_NAMES.map((bank) => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-medium text-slate-600 mb-1">계좌번호</label>
              <input
                type="text"
                {...register('bankAccount', {
                  pattern: {
                    value: /^[0-9-]*$/,
                    message: '계좌번호는 숫자와 하이픈만 입력 가능합니다.'
                  },
                  minLength: { value: 10, message: '계좌번호는 최소 10자 이상이어야 합니다.' },
                  maxLength: { value: 50, message: '계좌번호는 최대 50자 이하여야 합니다.' }
                })}
                readOnly={!isEditing}
                className={inputClass(!!errors.bankAccount)}
                placeholder="예: 123-456-789012"
                onInput={(e) => {
                  if (isEditing) {
                    const target = e.target as HTMLInputElement;
                    // 숫자와 하이픈만 허용
                    target.value = target.value.replace(/[^0-9-]/g, '');
                  }
                }}
              />
              {errors.bankAccount && (
                <p className="mt-1 text-sm text-red-600">{errors.bankAccount.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block font-medium text-slate-600 mb-1">컨설턴트 소개</label>
              <textarea
                {...register('consultantIntroduction', {
                  maxLength: { value: 500, message: '컨설턴트 소개는 500자 이내로 입력해주세요.' }
                })}
                readOnly={!isEditing}
                className={`${inputClass(!!errors.consultantIntroduction)} min-h-[100px] resize-none`}
                placeholder="컨설턴트의 전문 분야, 경력, 특징 등을 소개해주세요. (최대 500자)"
                maxLength={500}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.consultantIntroduction && (
                  <p className="text-sm text-red-600">{errors.consultantIntroduction.message}</p>
                )}
                <p className="text-xs text-slate-500 ml-auto">
                  {isEditing && (
                    <span>
                      {(watch('consultantIntroduction') || '').length}/500자
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 학력 정보 섹션 */}
        <section>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-base">학력</h3>
            {isEditing && (
              <button
                type="button"
                onClick={addEducation}
                className="px-3 py-1 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
              >
                + 추가
              </button>
            )}
          </div>
          
          <div className="mt-2 border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr className="text-left">
                  <th className="p-2 font-medium">학교명</th>
                  <th className="p-2 font-medium">전공</th>
                  <th className="p-2 font-medium">학위</th>
                  <th className="p-2 font-medium">입학일</th>
                  <th className="p-2 font-medium">졸업일</th>
                  {isEditing && <th className="w-12 p-2"></th>}
                </tr>
              </thead>
              <tbody>
                {educationFields.map((field, index) => (
                  <tr key={field.id} className="border-t">
                    <td className="p-1">
                      <input
                        type="text"
                        {...register(`education.${index}.school`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        {...register(`education.${index}.major`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        {...register(`education.${index}.degree`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="date"
                        {...register(`education.${index}.startDate`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent text-xs"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="date"
                        {...register(`education.${index}.graduationDate`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent text-xs"
                      />
                    </td>
                    {isEditing && (
                      <td className="p-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeEducation(index)}
                          className="text-slate-400 hover:text-rose-500"
                        >
                          &times;
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {educationFields.length === 0 && (
                  <tr>
                    <td colSpan={isEditing ? 6 : 5} className="p-4 text-center text-slate-500 text-sm">
                      학력 정보가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* 경력 정보 섹션 */}
        <section>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-base">경력</h3>
            {isEditing && (
              <button
                type="button"
                onClick={addExperience}
                className="px-3 py-1 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
              >
                + 추가
              </button>
            )}
          </div>
          
          <div className="mt-2 border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr className="text-left">
                  <th className="p-2 font-medium">회사명</th>
                  <th className="p-2 font-medium">부서</th>
                  <th className="p-2 font-medium">직급</th>
                  <th className="p-2 font-medium">입사일</th>
                  <th className="p-2 font-medium">퇴사일</th>
                  <th className="p-2 font-medium">연봉</th>
                  {isEditing && <th className="w-12 p-2"></th>}
                </tr>
              </thead>
              <tbody>
                {experienceFields.map((field, index) => (
                  <tr key={field.id} className="border-t">
                    <td className="p-1">
                      <input
                        type="text"
                        {...register(`experience.${index}.company`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        {...register(`experience.${index}.department`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent"
                      />
                    </td>
                    <td className="p-1">
                      {isEditing ? (
                        <select
                          {...register(`experience.${index}.position`)}
                          className="w-full border-0 rounded-md bg-transparent text-xs"
                        >
                          <option value="">선택</option>
                          {positions.map(pos => (
                            <option key={pos.id} value={pos.name}>
                              {pos.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          {...register(`experience.${index}.position`)}
                          readOnly
                          className="w-full border-0 rounded-md bg-transparent"
                        />
                      )}
                    </td>
                    <td className="p-1">
                      <input
                        type="date"
                        {...register(`experience.${index}.startDate`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent text-xs"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="date"
                        {...register(`experience.${index}.endDate`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent text-xs"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        {...register(`experience.${index}.annualSalary`)}
                        readOnly={!isEditing}
                        className="w-full border-0 rounded-md bg-transparent text-xs"
                        placeholder="예: 48,000,000"
                        onInput={(e) => {
                          if (isEditing) {
                            const target = e.target as HTMLInputElement;
                            // 숫자만 추출
                            const value = target.value.replace(/[^\d]/g, '');
                            // 세자리마다 콤마 추가
                            const formattedValue = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                            target.value = formattedValue;
                          }
                        }}
                      />
                    </td>
                    {isEditing && (
                      <td className="p-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeExperience(index)}
                          className="text-slate-400 hover:text-rose-500"
                        >
                          &times;
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {experienceFields.length === 0 && (
                  <tr>
                    <td colSpan={isEditing ? 7 : 6} className="p-4 text-center text-slate-500 text-sm">
                      경력 정보가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* 연봉 이력 섹션 */}
        {employee?.id && (
          <section>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 text-base">연봉 이력</h3>
              {isEditing && !isAddingSalary && (
                <button
                  type="button"
                  onClick={() => setIsAddingSalary(true)}
                  className="px-3 py-1 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                >
                  + 추가
                </button>
              )}
            </div>

            <div className="mt-2 border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr className="text-left">
                    <th className="p-2 font-medium w-24">연도</th>
                    <th className="p-2 font-medium">연봉</th>
                    {isEditing && <th className="w-24 p-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {isAddingSalary && isEditing && (
                    <tr className="border-t bg-blue-50">
                      <td className="p-2">
                        <select
                          value={newSalaryYear}
                          onChange={(e) => setNewSalaryYear(parseInt(e.target.value))}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          {(() => {
                            const joinYear = employee?.joinDate
                              ? new Date(employee.joinDate).getFullYear()
                              : 2020;
                            const years = [];
                            for (let y = 2030; y >= joinYear; y--) {
                              years.push(<option key={y} value={y}>{y}년</option>);
                            }
                            return years;
                          })()}
                        </select>
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          value={newSalaryValue}
                          onChange={(e) => setNewSalaryValue(formatSalary(e.target.value))}
                          placeholder="연봉 입력"
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleSaveSalary(newSalaryYear, newSalaryValue)}
                          className="text-green-600 hover:text-green-800 mr-2 text-sm"
                        >
                          저장
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingSalary(false);
                            setNewSalaryValue('');
                          }}
                          className="text-slate-400 hover:text-slate-600 text-sm"
                        >
                          취소
                        </button>
                      </td>
                    </tr>
                  )}
                  {salaryHistory.map((sh) => (
                    <tr key={sh.id} className="border-t">
                      <td className="p-2 text-sm font-medium">{sh.year}년</td>
                      <td className="p-2">
                        {editingSalaryYear === sh.year && isEditing ? (
                          <input
                            type="text"
                            value={editingSalaryValue}
                            onChange={(e) => setEditingSalaryValue(formatSalary(e.target.value))}
                            className="w-full px-2 py-1 border rounded text-sm"
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm">
                            {Number(sh.annualSalary).toLocaleString()}원
                          </span>
                        )}
                      </td>
                      {isEditing && (
                        <td className="p-2 text-center">
                          {editingSalaryYear === sh.year ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSaveSalary(sh.year, editingSalaryValue)}
                                className="text-green-600 hover:text-green-800 mr-2 text-sm"
                              >
                                저장
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingSalaryYear(null);
                                  setEditingSalaryValue('');
                                }}
                                className="text-slate-400 hover:text-slate-600 text-sm"
                              >
                                취소
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingSalaryYear(sh.year);
                                  setEditingSalaryValue(Number(sh.annualSalary).toLocaleString());
                                }}
                                className="text-blue-600 hover:text-blue-800 mr-2 text-sm"
                              >
                                수정
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteSalary(sh.year)}
                                className="text-rose-500 hover:text-rose-700 text-sm"
                              >
                                삭제
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {salaryHistory.length === 0 && !isAddingSalary && (
                    <tr>
                      <td colSpan={isEditing ? 3 : 2} className="p-4 text-center text-slate-500 text-sm">
                        연봉 이력이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* 첨부문서 섹션 */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-800">
              첨부문서 {documents.length > 0 && <span className="text-sm text-slate-500">({documents.length})</span>}
            </h3>
            {documents.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-50 flex items-center space-x-1"
              >
                {downloadingAll ? (
                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                <span>{downloadingAll ? '다운로드 중...' : '전체 다운로드'}</span>
              </button>
            )}
          </div>

          {/* 문서 목록 */}
          {documents.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded border hover:border-indigo-200 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <svg className="h-4 w-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-slate-700 truncate">{doc.originalName}</span>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {(doc.fileSize / 1024 / 1024).toFixed(1)} MB
                        </span>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(doc.createdAt).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-3">
                      <button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                        title="다운로드"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      {replacingDocId === doc.id ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleReplaceDocument(doc.id, file);
                            }}
                            className="text-xs w-32"
                          />
                          <button
                            type="button"
                            onClick={() => setReplacingDocId(null)}
                            className="p-1 text-slate-400 hover:text-slate-600"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setReplacingDocId(doc.id)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                          title="파일 교체"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      )}
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => handleDocumentDelete(doc.id)}
                          className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                          title="삭제"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {documents.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              등록된 첨부문서가 없습니다.
            </div>
          )}

          {/* 새 문서 업로드 (편집 모드에서만) */}
          {isEditing && employee?.id && (
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">문서 추가</h4>
              <div className="space-y-3">
                <div className="flex space-x-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      문서 유형
                    </label>
                    <select
                      value={newDocument.documentType}
                      onChange={(e) => setNewDocument(prev => ({
                        ...prev,
                        documentType: e.target.value as DocumentType
                      }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    >
                      <option value={DocumentType.CONTRACT}>근로계약서</option>
                      <option value={DocumentType.SALARY_CONTRACT}>연봉계약서</option>
                      <option value={DocumentType.PRIVACY}>개인정보 동의서</option>
                      <option value={DocumentType.NDA}>영업비밀 서약서</option>
                      <option value={DocumentType.PROBATION}>수습평가 동의서</option>
                      <option value={DocumentType.EQUIPMENT}>전자기기 확인서</option>
                      <option value={DocumentType.RESUME}>이력서</option>
                      <option value={DocumentType.DIPLOMA}>졸업증명서</option>
                      <option value={DocumentType.CAREER_CERT}>경력증명서</option>
                      <option value={DocumentType.LICENSE}>자격증</option>
                      <option value={DocumentType.RESIDENT_COPY}>주민등록등초본</option>
                      <option value={DocumentType.BANK_ACCOUNT}>통장사본</option>
                      <option value={DocumentType.PROFILE_PHOTO}>프로필 사진</option>
                      <option value={DocumentType.INTRO_PPT}>소개자료</option>
                      <option value={DocumentType.OTHER}>기타</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      파일
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 10 * 1024 * 1024) {
                            showToast('파일 크기는 10MB 이하여야 합니다.', 'error');
                            return;
                          }
                          setNewDocument(prev => ({ ...prev, file }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    설명 (선택사항)
                  </label>
                  <textarea
                    value={newDocument.description}
                    onChange={(e) => setNewDocument(prev => ({ 
                      ...prev, 
                      description: e.target.value 
                    }))}
                    maxLength={500}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    placeholder="문서에 대한 간단한 설명을 입력하세요..."
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    {newDocument.description.length}/500
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDocumentUpload}
                  disabled={!newDocument.file || uploadingDoc}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {uploadingDoc && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {uploadingDoc ? '업로드 중...' : '업로드'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* 액션 버튼 */}
        {isEditing && (
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
            >
              {isSubmitting && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {mode === 'new' ? '직원 추가' : '수정 완료'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

// 기존 getEmployeeFormData 함수는 제거되고 폼 제출 시 onSubmit으로 데이터 전달
export default EmployeeForm;